import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Breadcrumb,
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  message,
  Modal,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
} from "antd";
import { Link, useNavigate } from "react-router-dom";
import {
  DeleteOutlined,
  EditOutlined,
  FilterOutlined,
  LockOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { debounce } from "lodash";
import { useTranslation } from "react-i18next";
import ButtonField from "../../common/htmlComponents/ButtonField";
import { deleteRequest, getRequest, patchRequest, postRequest } from "../../axios";
import { ReactComponent as PasswordIcon } from "../../image/Password.svg";
import {
  ErrorNotificationMsg,
  ShowErrorMsg,
  SuccessNotificationMsg,
} from "../../utils/Notifications";

const GeneralVendorList = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const formRef = useRef();

  const [clickedFilterButton, setClickedFilterButton] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [tableData, setTableData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordRecord, setPasswordRecord] = useState(null);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordForm] = Form.useForm();
  const [searchInputValue, setSearchInputValue] = useState("");
  const [state, setState] = useState({
    page: 1,
    pagesize: 10,
    search: "",
    start_date: "",
    end_date: "",
  });

  const handleStartDateChange = (date) => {
    setStartDate(date ? moment(date).format("YYYY-MM-DD") : "");
  };

  const handleEndDateChange = (date) => {
    setEndDate(date ? moment(date).format("YYYY-MM-DD") : "");
  };

  const handleApplyFilter = () => {
    setClickedFilterButton(true);
    setState((prev) => ({
      ...prev,
      page: 1,
      start_date: startDate,
      end_date: endDate,
    }));
  };

  const handleClearFields = () => {
    setStartDate("");
    setEndDate("");
    formRef.current?.setFieldsValue({
      start_date: null,
      end_date: null,
    });
  };

  const handleReset = () => {
    setClickedFilterButton(false);
    setStartDate("");
    setEndDate("");
    setSearchInputValue("");
    setState({
      page: 1,
      pagesize: 10,
      search: "",
      start_date: "",
      end_date: "",
    });
    formRef.current?.resetFields();
  };

  const { Search } = Input;

  const applySearch = (value) => {
    setState((prev) => ({
      ...prev,
      page: 1,
      search: value || "",
    }));
  };

  const debouncedSearchRef = useRef(
    debounce((value) => applySearch(value), 600),
  );

  useEffect(
    () => () => {
      debouncedSearchRef.current?.cancel?.();
    },
    [],
  );

  const onSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchInputValue(value);
    debouncedSearchRef.current?.(value);
  };

  const onSearchSubmit = (value) => {
    debouncedSearchRef.current?.cancel?.();
    setSearchInputValue(value || "");
    applySearch(value || "");
  };

  const fetchGeneralVendors = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: state.page,
        pagesize: state.pagesize,
      };
      if (state.search?.trim()) params.search = state.search.trim();
      if (state.start_date) params.start_date = state.start_date;
      if (state.end_date) params.end_date = state.end_date;

      const res = await getRequest("general-procurement-vendor/", params);
      const payload = res?.data;

      const count =
        payload?.count ??
        payload?.results?.count ??
        payload?.results?.data?.length ??
        (Array.isArray(payload?.results) ? payload.results.length : 0);
      setTotalCount(count);

      const results = Array.isArray(payload?.results?.data)
        ? payload.results.data
        : Array.isArray(payload?.results)
          ? payload.results
          : Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload)
              ? payload
              : [];
      setTableData(results);
    } catch (err) {
      ShowErrorMsg(err);
      setTableData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [
    state.page,
    state.pagesize,
    state.search,
    state.start_date,
    state.end_date,
  ]);

  useEffect(() => {
    fetchGeneralVendors();
  }, [fetchGeneralVendors]);

  const handleEdit = useCallback(
    (record) => {
      const id = record?.id;
      if (id == null || id === "") {
        message.warning("Missing vendor id for edit.");
        return;
      }
      navigate(`/general-vendor/edit/${id}`);
    },
    [navigate],
  );

  const handleDelete = useCallback(
    (record) => {
      const id = record?.id;
      if (id == null || id === "") {
        message.warning("Missing vendor id for delete.");
        return;
      }
      Modal.confirm({
        title: "Delete this vendor?",
        content:
          "This will permanently remove the vendor. This action cannot be undone.",
        okText: "Delete",
        okType: "danger",
        cancelText: "Cancel",
        onOk: async () => {
          try {
            await deleteRequest(`general-procurement-vendor/${id}/`);
            SuccessNotificationMsg("Vendor deleted successfully.");
            fetchGeneralVendors();
          } catch (err) {
            ShowErrorMsg(err);
          }
        },
      });
    },
    [fetchGeneralVendors],
  );

  const getVendorCompanyName = (record) =>
    record?.vendor_registered_name ??
    record?.vendor_trade_name ??
    record?.name ??
    record?.vendor_name ??
    record?.business_name ??
    null;

  const getVendorPhone = (record) =>
    record?.phone ??
    record?.mobile_number ??
    record?.mobile ??
    record?.bankdetails?.phone_number ??
    record?.suppliers_billing_details?.mobile_number ??
    null;

  const isVendorActive = (record) =>
    record?.is_active === true ||
    String(record?.status ?? "").toLowerCase() === "active";

  const renderStatus = (record) => {
    const raw = String(record?.status ?? "").toLowerCase();
    if (raw === "pending") {
      return <Tag color="orange">Pending</Tag>;
    }
    if (raw === "rejected") {
      return <Tag color="red">Rejected</Tag>;
    }
    if (raw === "approved") {
      return <Tag color="blue">Approved</Tag>;
    }
    if (raw === "active") {
      return <Tag color="green">Active</Tag>;
    }
    if (raw === "inactive") {
      return <Tag color="default">Inactive</Tag>;
    }
    return <Tag>{record?.status ?? "—"}</Tag>;
  };

  const handleToggleActiveStatus = useCallback(
    (record, nextActive) => {
      const id = record?.id;
      if (id == null || id === "") {
        message.warning("Missing vendor id.");
        return;
      }

      const companyName = getVendorCompanyName(record) ?? "this vendor";
      const actionLabel = nextActive ? "activate" : "deactivate";

      Modal.confirm({
        title: nextActive ? "Activate vendor?" : "Deactivate vendor?",
        content: `Are you sure you want to ${actionLabel} ${companyName}?`,
        okText: nextActive ? "Activate" : "Deactivate",
        okType: nextActive ? "primary" : "danger",
        cancelText: "Cancel",
        onOk: async () => {
          setStatusUpdatingId(id);
          try {
            await patchRequest("general-procurement-vendor/update-status/", {
              vendor_id: id,
              status: nextActive ? "active" : "inactive",
            });
            SuccessNotificationMsg(
              `Vendor ${nextActive ? "activated" : "deactivated"} successfully.`,
            );
            fetchGeneralVendors();
          } catch (err) {
            ShowErrorMsg(err);
          } finally {
            setStatusUpdatingId(null);
          }
        },
      });
    },
    [fetchGeneralVendors],
  );

  const openPasswordModal = (record) => {
    setPasswordRecord(record);
    setPasswordModalOpen(true);
  };

  const handlePasswordModalAfterOpen = (open) => {
    if (open) {
      passwordForm.resetFields();
    }
  };

  const preventPasswordAutofill = (event) => {
    event.target.removeAttribute("readonly");
  };

  const closePasswordModal = () => {
    setPasswordModalOpen(false);
    setPasswordRecord(null);
    passwordForm.resetFields();
  };

  const handleSetPassword = async (values) => {
    setPasswordLoading(true);
    try {
      const apiResponse = await postRequest("general-vendor-reset-password/", {
        vendor_id: passwordRecord?.id,
        password1: values.password,
        password2: values.confirm_password,
      });

      if (apiResponse.data?.success) {
        SuccessNotificationMsg(
          apiResponse.data.message || "Password set successfully",
        );
        closePasswordModal();
        fetchGeneralVendors();
      } else {
        ErrorNotificationMsg(
          apiResponse.data?.message || "Failed to set password",
        );
      }
    } catch (error) {
      ErrorNotificationMsg("Failed to set password");
    } finally {
      setPasswordLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        title: "Company Name",
        key: "company_name",
        width: 220,
        render: (_, record) => getVendorCompanyName(record) ?? "—",
      },
      {
        title: "Person Name",
        dataIndex: "person_name",
        key: "person_name",
        width: 180,
        render: (v, record) =>
          v ?? record?.contact_person ?? record?.person ?? "—",
      },
      {
        title: "Mob No.",
        key: "phone",
        width: 150,
        render: (_, record) => getVendorPhone(record) ?? "—",
      },
      {
        title: "Email Id",
        dataIndex: "email",
        key: "email",
        width: 220,
        render: (v) => v ?? "—",
      },
      {
        title: "GST No.",
        dataIndex: "gst_no",
        key: "gst_no",
        width: 180,
        render: (v) => (v ? v : "—"),
      },
      {
        title: "Status",
        key: "status",
        width: 130,
        render: (_, record) => renderStatus(record),
      },
      {
        title: "Active / Inactive",
        key: "is_active",
        width: 140,
        align: "center",
        render: (_, record) => (
          <Switch
            checked={isVendorActive(record)}
            checkedChildren="Active"
            unCheckedChildren="Inactive"
            loading={statusUpdatingId === record?.id}
            onChange={(checked) => handleToggleActiveStatus(record, checked)}
          />
        ),
      },
      {
        title: "Action",
        key: "operation",
        fixed: "right",
        width: 130,
        render: (_, record) => (
          <Space size="small">
            <Tooltip title="Edit" color="#094E8F">
              <Button
                type="text"
                size="small"
                icon={<EditOutlined style={{ color: "#094E8F" }} />}
                onClick={() => handleEdit(record)}
                aria-label="Edit vendor"
              />
            </Tooltip>
            <Tooltip title="Set Password" color="#094E8F">
              <Button
                type="text"
                size="small"
                icon={<PasswordIcon style={{ width: 14, height: 14 }} />}
                onClick={() => openPasswordModal(record)}
                aria-label="Set vendor password"
              />
            </Tooltip>
            <Tooltip title="Delete" color="#094E8F">
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record)}
                aria-label="Delete vendor"
              />
            </Tooltip>
          </Space>
        ),
      },
    ],
    [handleEdit, handleDelete, handleToggleActiveStatus, statusUpdatingId],
  );

  return (
    <>
      <div className="ListingTopBar">
        <div className="pagenamewrap">
          <div className="pagename">
            <h3>General Vendors — History</h3>
            <Breadcrumb className="Breadcrumb-Sec">
              <Breadcrumb.Item>
                <Link to="/dashboard">Dashboard</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>General Vendors — History</Breadcrumb.Item>
            </Breadcrumb>
          </div>

          <Space>
            <ButtonField
              className="SaveGradiantBtn"
              type="primary"
              htmlType="button"
              label="Add Vendor"
              onClick={() => navigate("/general-vendor/add")}
            />
          </Space>
        </div>
      </div>

      <section className="grid-sec mt-5 tablegridsec">
        <div className="PR-body">
          <div className="filterrow">
            <div className="filtercol">
              <Form autoComplete="off" ref={formRef}>
                <Space wrap>
                  <Col xs={24} className="TextFeild gutter-row">
                    <Form.Item name="start_date">
                      <DatePicker
                        format="DD-MM-YY"
                        placeholder="Created from"
                        onChange={handleStartDateChange}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} className="TextFeild gutter-row">
                    <Form.Item name="end_date">
                      <DatePicker
                        format="DD-MM-YY"
                        placeholder="Created to"
                        onChange={handleEndDateChange}
                      />
                    </Form.Item>
                  </Col>

                  <Button
                    type="primary"
                    htmlType="button"
                    icon={
                      <Tooltip title="Apply" color="#094E8F">
                        <FilterOutlined style={{ fontSize: "20px" }} />
                      </Tooltip>
                    }
                    style={{
                      backgroundColor: "#094e8f",
                      color: "#ffffff",
                      border: "none",
                      width: "40px",
                      height: "40px",
                      boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.1)",
                      borderRadius: "4px",
                    }}
                    onClick={startDate || endDate ? handleApplyFilter : null}
                  />
                  <Button
                    type="primary"
                    htmlType="button"
                    icon={
                      <Tooltip title="Reset" color="#094E8F">
                        <UndoOutlined style={{ fontSize: "20px" }} />
                      </Tooltip>
                    }
                    style={{
                      backgroundColor: "#094e8f",
                      color: "#ffffff",
                      border: "none",
                      width: "40px",
                      height: "40px",
                      boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.1)",
                      borderRadius: "4px",
                    }}
                    onClick={
                      clickedFilterButton ? handleReset : handleClearFields
                    }
                  />
                </Space>
              </Form>
            </div>

            <div className="btnscol">
              <Space>
                <div className="searchwrp">
                  <Space direction="vertical">
                    <Search
                      placeholder="Search"
                      onSearch={onSearchSubmit}
                      allowClear
                      style={{ width: 250 }}
                      value={searchInputValue}
                      onChange={onSearchInputChange}
                    />
                  </Space>
                </div>
              </Space>
            </div>
          </div>

          <div className="tablewrp">
            <Table
              scroll={{ x: 1250 }}
              columns={columns}
              dataSource={tableData}
              loading={loading}
              rowKey={(record) =>
                record?.id ??
                record?.email ??
                `${getVendorCompanyName(record) ?? ""}-${getVendorPhone(record) ?? ""}`
              }
              pagination={{
                current: state.page,
                pageSize: state.pagesize,
                total: totalCount,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50"],
                locale: { items_per_page: "Items / Page" },
                showTotal: (total) => `Total ${total} items`,
              }}
              onChange={(pagination) => {
                setState((prev) => ({
                  ...prev,
                  page: pagination.current,
                  pagesize: pagination.pageSize,
                }));
              }}
            />
          </div>
        </div>
      </section>

      <Modal
        className="ModelPopupSec"
        title="Set Password"
        open={passwordModalOpen}
        onCancel={closePasswordModal}
        afterOpenChange={handlePasswordModalAfterOpen}
        footer={null}
        destroyOnClose
        wrapClassName="modal-wrapper"
        keyboard={false}
      >
        {passwordRecord && (
          <Form
            form={passwordForm}
            layout="vertical"
            onFinish={handleSetPassword}
            autoComplete="off"
          >
            <Form.Item label="Email">
              <Input
                value={passwordRecord.email || ""}
                disabled
                autoComplete="off"
                readOnly
              />
            </Form.Item>
            <Form.Item
              name="password"
              label="Password"
              rules={[
                { required: true, message: t("val_password") },
                {
                  pattern:
                    /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$.=!_%*#:?&])[A-Za-z\d@$.=!_%*#:?&]{8,}$/,
                  message: t("passsword_conditions"),
                },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined className="site-form-item-icon" />}
                placeholder="Enter password"
                autoComplete="new-password"
                readOnly
                onFocus={preventPasswordAutofill}
              />
            </Form.Item>
            <Form.Item
              name="confirm_password"
              label="Confirm Password"
              dependencies={["password"]}
              rules={[
                { required: true, message: "Please confirm the password" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const password = getFieldValue("password");
                    if (!value || password === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error("Passwords do not match"));
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined className="site-form-item-icon" />}
                placeholder="Confirm password"
                autoComplete="new-password"
                readOnly
                onFocus={preventPasswordAutofill}
              />
            </Form.Item>
            <div className="bottomButtons" style={{ textAlign: "right" }}>
              <Space>
                <ButtonField
                  type="primary"
                  className="CancelGradiantBtn"
                  htmlType="button"
                  label={t("cancel")}
                  onClick={closePasswordModal}
                />
                <ButtonField
                  type="primary"
                  className="SaveGradiantBtn"
                  htmlType="submit"
                  label="Set Password"
                  loading={passwordLoading}
                />
              </Space>
            </div>
          </Form>
        )}
      </Modal>
    </>
  );
};

export default GeneralVendorList;
